import React from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";

import { useState } from "react";

import { CustomText } from "../../../../components/StyledComponents/styleComponents.index";

import { createPercentFormatter, createIntFormatter } from "../../../../utils/formatter/formatter";

const GRAPHIC_HEIGHT=420;

const getAgeColor = (age) => {
  if (age < 100) return "#F4E04D"; 
  if (age < 200) return "#7BC96F"; 
  if (age < 350) return "#4AA3A3"; 
  return "#6A1B9A";                
};

const OpportunityBubbleTooltip = ({ active, payload, hoverColor }) => {
  if (!active || !payload?.length) return null;

  const data = payload[0].payload;

  return (
    <div
      style={{
        background: hoverColor ?? "#eee",
        padding: "10px 12px",
        borderRadius: "2px",
        color: "#000",
        fontSize: "0.75rem",
        minWidth: "260px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      }}
    >
      <div style={{ fontWeight: 800, marginBottom: "6px" }}>
        {data.level}
      </div>

      <div>cobertura_projetada_forecast={data.inventoryCoverageRatio}</div>
      <div>sell-through = {createPercentFormatter().format(data.sellThrough)}</div>
      <div>estoque atual = {createIntFormatter().format(data.inventoryQty)}</div>
      <div>idade média = {createIntFormatter().format(data.averageInventoryAge)} dias</div>
    </div>
  );
};

const OpportunityBubbleChart = ({ ctx, uiState }) => {
  const [hoverColor, setHoverColor] = useState(null);

  const { data, medians, domains } = ctx || {};
  const { mode, fixedMedianKey } = uiState || {};

  const xDomain = domains?.x?.p5 != null && domains?.x?.p95 != null
    ? [domains.x.p5, domains.x.p95]
    : [0, 12];

  const yDomain = domains?.y?.p5 != null && domains?.y?.p95 != null
    ? [domains.y.p5, domains.y.p95]
    : [0, 1];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gridTemplateRows: '1fr, 3fr' }}>

        <CustomText color="charcoalGray" weight="bold" textAlign="start" fontSize={['0.3rem', '0.7rem', '1rem']}>
          {'Mapa de Oportunidades por Família'}
        </CustomText>

        <ResponsiveContainer width="100%" height={GRAPHIC_HEIGHT}>
          <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 20 }}>
            
          <CartesianGrid stroke="#EAEAEA" strokeDasharray="3 3" />

          <XAxis
            type="number"
            dataKey="inventoryCoverageRatio"
            domain={xDomain}
            tickFormatter={(v) => Math.round(v)}
            label={{
              value: "Cobertura projetada",
              position: "bottom",
              offset: 20 
            }}/>

            <YAxis
              type="number"
              dataKey="sellThrough"
              domain={yDomain}
              tickFormatter={(v) => v.toFixed(2)}
              label={{
                  value: "Sell-through",
                  angle: -90,
                  position: "insideLeft",
                  textAnchor: "middle",
                  dy: 0,
              }}/>

            <ZAxis dataKey="inventoryQty" range={[60, 600]} />

            {medians.map((median) => (
              <React.Fragment key={`median-${median.key}`}>
                <ReferenceLine
                  x={median.median_inventoryCoverageRatio}
                  stroke="#FF4D4F"
                  strokeDasharray="6 6"
                  strokeOpacity={0.8}

                  ifOverflow="extendDomain"
                />

                <ReferenceLine
                  y={median.median_sellThrough}
                  stroke="#FF4D4F"
                  strokeDasharray="6 6"
                  strokeOpacity={0.8}

                  ifOverflow="extendDomain"
                />
              </React.Fragment>
            ))}              

            <Tooltip
                cursor={false}
                content={(props) => (
                <OpportunityBubbleTooltip {...props} hoverColor={hoverColor} />
                )}
            />
            
            <Scatter
              key={'bubble'}
              name={`Horizon ${uiState?.defaultHorizon || ""}`}
              data={data}
              onMouseEnter={(point) =>
                setHoverColor(getAgeColor(point.payload.averageInventoryAge))
              }
              onMouseLeave={() => setHoverColor(null)}
              shape={({ cx, cy, payload, size }) => (
                <circle
                  cx={cx}
                  cy={cy}
                  r={Math.sqrt(size)}
                  fill={getAgeColor(payload.averageInventoryAge)}
                  fillOpacity={0.75}
                  stroke="#FFF"
                />
              )}
            />
            
            </ScatterChart>
        </ResponsiveContainer>
    </div>
  );
};

const AgeColorLegend = () => {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gridTemplateRows: '1fr, 3fr' }}>

        <CustomText color="charcoalGray" weight="bold" textAlign="start" fontSize={['0.1rem', '0.6rem', '0.8rem']}>
            {'Idade ponderada'}
        </CustomText>

        <div
        style={{
            display: "flex",
            alignItems: "stretch",
            gap: "8px",
            height: `${GRAPHIC_HEIGHT.toString()}px`,
        }}
        >
        <div
            style={{
            width: "18px",
            borderRadius: "10px",
            background: "linear-gradient(to top, #F4E04D, #7BC96F, #4AA3A3, #6A1B9A)",
            }}
        />

        <div
            style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            fontSize: "0.75rem",
            color: "#555",
            fontWeight: "600",
            }}
        >
            <span>600+</span>
            <span>450</span>
            <span>300</span>
            <span>150</span>
            <span>0</span>
        </div>
        </div>
    </div>
  );
};

export const InventoryBubbleMetrics = ({ ctx, uiState }) => {

    const {
      data,
      medians,
      domains
    } = ctx || {};

    const {
      groupBy,
      defaultHorizon,
      mode,
      fixedMedianKey
    } = uiState || {}

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '6fr 1fr', gap: '4rem' }}>
            <OpportunityBubbleChart 
              ctx={{ data: data, medians: medians, domains: domains }} 
              uiState={{ groupBy: groupBy, defaultHorizon: defaultHorizon, mode: mode, fixedMedianKey: fixedMedianKey }}
            />
            <AgeColorLegend />
        </div>
    )
};